import { WritableQuoteBuilderStateType } from '../../types/state';
import {
  type IssueDeleteOptions,
  IssueInsertType,
  IssueType,
} from '../../types/state/issues';

/**
 * Retrieves an issue from the state.
 *
 * @param state The state to retrieve the issue from
 * @param issueId The id of the issue to retrieve
 * @returns The issue or undefined if the issue was not found
 */
export function getIssue(
  state: WritableQuoteBuilderStateType,
  issueId: string,
): IssueType | undefined {
  return state.issues[issueId];
}

/**
 * Retrieves an issue from the state or throws an error if the issue does not exist.
 *
 * @param state The state to retrieve the issue from
 * @param issueId The id of the issue to retrieve
 * @returns The issue
 * @throws {Error} Issue ${issueId} not found
 */
export function getIssueOrThrow(
  state: WritableQuoteBuilderStateType,
  issueId: string,
): IssueType {
  const issue = getIssue(state, issueId);

  if (!issue) {
    throw new Error(`Issue ${issueId} not found`);
  }

  return issue;
}

/**
 * Upserts an issue into the state.
 *
 * @param state The state to upsert the issue into
 * @param issueId The id of the issue to upsert
 * @param issue The issue to upsert
 */
export function upsertIssue(
  state: WritableQuoteBuilderStateType,
  issueId: string,
  issue: IssueInsertType,
) {
  const existingIssue = getIssue(state, issueId);

  if (!existingIssue) {
    state.issues[issueId] = {
      ...issue,
      id: issueId,
      childIssueIds: issue.childIssueIds ?? [],
      siblingIssueIds: issue.siblingIssueIds ?? [],
    };
    return;
  }

  const childIssueIds = new Set(
    existingIssue.childIssueIds.concat(issue.childIssueIds ?? []),
  );
  const siblingIssueIds = new Set(
    existingIssue.siblingIssueIds.concat(issue.siblingIssueIds ?? []),
  );

  state.issues[issueId] = {
    ...existingIssue,
    ...issue,
    childIssueIds: [...childIssueIds],
    siblingIssueIds: [...siblingIssueIds],
  };
}

/**
 * Deletes an issue from the state.
 *
 * This function will recursively delete child and sibling issues as well, ultimately deleting
 * its parent issue if the parent has no other child issues.
 *
 * @param state The state to delete the issue from
 * @param issueId The id of the issue to delete
 */
export function deleteIssue(
  state: WritableQuoteBuilderStateType,
  issueId: string,
  options: IssueDeleteOptions = { deleteParent: true },
) {
  const issue = getIssue(state, issueId);

  if (!issue) {
    return;
  }

  // First delete child issues
  for (const childIssueId of issue.childIssueIds) {
    deleteIssue(state, childIssueId, { deleteParent: false });
  }

  // Delete the current issue first
  delete state.issues[issueId];

  // Remove this issue from all sibling issues
  if (issue.siblingIssueIds.length > 0) {
    for (const siblingIssueId of issue.siblingIssueIds) {
      const sibling = getIssueOrThrow(state, siblingIssueId);

      sibling.siblingIssueIds = sibling.siblingIssueIds?.filter(
        (siblingIssueId) => siblingIssueId !== issueId,
      );

      // Delete all sibling issues that no longer have any siblings
      if (!sibling.siblingIssueIds?.length) {
        deleteIssue(state, siblingIssueId, { deleteParent: false });
      }
    }
  }

  // Remove this issue from the the parent's child issues
  if (issue.parentIssueId) {
    const parent = getIssueOrThrow(state, issue.parentIssueId);

    // Remove the current issue from the parent's child issue list
    parent.childIssueIds = parent.childIssueIds?.filter(
      (childIssueId) => childIssueId !== issueId,
    );

    // If the list is now empty, delete the parent
    if (!parent.childIssueIds?.length && options.deleteParent) {
      deleteIssue(state, issue.parentIssueId);
    }
  }
}

export function checkCanViewPDF(state: WritableQuoteBuilderStateType) {
  state.canViewPDF = !Object.values(state.issues).some(
    (issue) => issue.severity === 'error',
  );
}
